import type { ProxyJob, FollowUpTemplateId } from './mockProxyJobs';
import { bandFor, daysToMeeting } from '../utils/proxyDecisioning';

interface Template { subject: string; body: string; }

export const PROXY_TEMPLATES: Record<FollowUpTemplateId, Template> = {
  'FU-American': {
    subject: 'Proxy Material Follow-Up - {{issuer}} (Job {{jobId}})',
    body: 'Hello,\n\nWe are following up on outstanding proxy material for the below job. Please advise on status and expected ship date.\n\nJob #: {{jobId}}\nIssuer: {{issuer}}\nMeeting Date: {{meetingDate}}\nFull Sets Needed: {{fullSetsNeeded}}\n\nThank you,\nICT / Collections',
  },
  'FU-Canadian': {
    subject: 'Proxy Material Follow-Up (Canada) - {{issuer}} (Job {{jobId}})',
    body: 'Hello,\n\nFollowing up on outstanding proxy/circular material for the below Canadian job. Please confirm the intermediary mail date and provide Full Set + NAA quantities.\n\nJob #: {{jobId}}\nIssuer: {{issuer}}\nMeeting Date: {{meetingDate}}\nFull Sets Needed: {{fullSetsNeeded}}\nNAA Needed: {{naaNeeded}}\n\nThank you,\nICT / Collections',
  },
  // ICT-1: Broadridge is checked on the RICS portal; this email only goes out
  // when RICS shows the project as closed.
  'FU-Broadridge': {
    subject: 'Proxy Material Follow-Up - {{issuer}} (Job {{jobId}})',
    body: 'Hello,\n\nPlease advise why this job is showing as closed in the Broadridge system.\n\nBroadridge Job #: {{ricsJobNumber}}\nJob #: {{jobId}}\nIssuer: {{issuer}}\nMeeting Date: {{meetingDate}}\nFull Sets Needed: {{fullSetsNeeded}}\n\nThank you,\nICT / Collections',
  },
  // ICT-8: Red follow-ups use their own template.
  'FU-Red': {
    subject: 'URGENT: Proxy Material Outstanding - {{issuer}} (Job {{jobId}})',
    body: 'Hello,\n\nWe still have not received proxy material for the below job, and the meeting is approaching. Please send the material, or advise on status and expected ship date, as soon as possible.\n\nJob #: {{jobId}}\nIssuer: {{issuer}}\nMeeting Date: {{meetingDate}}\nFull Sets Needed: {{fullSetsNeeded}}\n\nThank you,\nICT / Collections',
  },
  'FU-Issuer': {
    subject: 'Proxy Material Request - {{issuer}} (Job {{jobId}})',
    body: 'Hello,\n\nWe do not have a solicitor of record for this job and are requesting proxy material directly from the issuer (via Mediant). Please also confirm billing contact.\n\nJob #: {{jobId}}\nIssuer: {{issuer}}\nMeeting Date: {{meetingDate}}\nFull Sets Needed: {{fullSetsNeeded}}\n\nThank you,\nICT / Collections',
  },
  'Material-Shortage': {
    subject: 'Material Shortage Follow-Up - {{issuer}} (Job {{jobId}})',
    body: 'Hello,\n\nOur records show a shortage on the below job. Please advise on reorder/POD status for the shorted material.\n\nJob #: {{jobId}}\nIssuer: {{issuer}}\n\nThank you,\nICT / Collections',
  },
};

// Precedence (spec doesn't give an explicit order — this is a documented,
// defensible default): Shortage > no-solicitor (Issuer) > Red (ICT-8) >
// Broadridge > CA > US.
export function selectTemplate(job: ProxyJob): FollowUpTemplateId {
  if (job.materialStatus === 'Shortage') return 'Material-Shortage';
  if (job.solicitor === null) return 'FU-Issuer';
  if (bandFor(daysToMeeting(job)) === 'Red') return 'FU-Red';
  if (job.isBroadridge) return 'FU-Broadridge';
  if (job.country === 'CA') return 'FU-Canadian';
  return 'FU-American';
}

function interpolate(text: string, job: ProxyJob): string {
  return text
    .replace(/{{issuer}}/g, job.issuer)
    .replace(/{{jobId}}/g, job.jobId)
    .replace(/{{meetingDate}}/g, job.meetingDate)
    .replace(/{{fullSetsNeeded}}/g, String(job.fullSetsNeeded))
    .replace(/{{naaNeeded}}/g, String(job.naaNeeded))
    .replace(/{{ricsJobNumber}}/g, job.ricsJobNumber || 'Not on file');
}

export function renderTemplate(templateId: FollowUpTemplateId, job: ProxyJob): Template {
  const t = PROXY_TEMPLATES[templateId];
  return { subject: interpolate(t.subject, job), body: interpolate(t.body, job) };
}

// ICT-1 / ICT-8: one email per solicitor, with every job in their section
// pasted into the template's grid (report columns B to H).
const GRID_INTRO: Partial<Record<FollowUpTemplateId, { subject: string; intro: string }>> = {
  'FU-American': { subject: 'Proxy Material Follow-Up', intro: 'We are following up on outstanding proxy material for the jobs below. Please advise on status and expected ship date.' },
  'FU-Canadian': { subject: 'Proxy Material Follow-Up (Canada)', intro: 'Following up on outstanding proxy/circular material for the Canadian jobs below. Please confirm the intermediary mail date and provide Full Set + NAA quantities.' },
  'FU-Red': { subject: 'URGENT: Proxy Material Outstanding', intro: 'We still have not received proxy material for the jobs below, and their meetings are approaching. Please send the material, or advise on status and expected ship date, as soon as possible.' },
};

export function renderGridTemplate(templateId: FollowUpTemplateId, solicitor: string, jobs: ProxyJob[]): Template {
  const t = GRID_INTRO[templateId] ?? GRID_INTRO['FU-American']!;
  const canadian = templateId === 'FU-Canadian';
  const header = canadian ? 'Job # | Issuer | CUSIP | Meeting Date | Full Sets | NAA' : 'Job # | Issuer | CUSIP | Meeting Date | Full Sets';
  const rows = jobs.map(j => [j.jobId, j.issuer, j.cusip, j.meetingDate, j.fullSetsNeeded, ...(canadian ? [j.naaNeeded] : [])].join(' | '));
  const notes = jobs.filter(j => j.followUpMailingMaterial).map(j => `Job ${j.jobId} is a follow-up mailing. The material being mailed is the ${j.followUpMailingMaterial}.`);
  return {
    subject: `${t.subject}: ${solicitor} (${jobs.length} jobs)`,
    body: `Hello,\n\n${t.intro}\n\n${header}\n${rows.join('\n')}${notes.length ? `\n\n${notes.join('\n')}` : ''}\n\nThank you,\nICT / Collections`,
  };
}
